/*
 * Cart entries preview client.
 *
 * Hydrates the entries banner and applied-code pills from the SHFTD backend's
 * Shopify App Proxy endpoint:
 *
 *   POST /apps/shftd/calculate-entries
 *
 * Triggers:
 *  - cart drawer / cart page mount (DOMContentLoaded + on dispatch)
 *  - listening for cart-update events (Dawn-style themes dispatch
 *    `cart:update` and/or `published:` events; we listen broadly to be
 *    resilient to theme variants).
 *  - explicit refresh after the user types a code into the input.
 *
 * All write paths use textContent / setAttribute. We never inject API
 * response strings via innerHTML — any merchant-controlled `label` field
 * stays in textContent so it can't introduce script payloads.
 *
 * Defensive choices:
 *  - Aborts and falls back silently on any non-2xx response. SSR Liquid
 *    placeholders remain visible, so users still see a number.
 *  - Per-tab `AbortController` cancels in-flight requests when the cart
 *    updates again before the previous request returned.
 */

(function () {
  "use strict";

  const PROXY_PATH = "/apps/shftd/calculate-entries";
  const CART_FETCH_PATH = "/cart.js";
  const FETCH_TIMEOUT_MS = 8000;
  // Sentinel for the synthetic cumulative Cart Tiers effect (mirrors the
  // backend's CART_TIERS_CODE). Capsule boost stacks it on top of real codes.
  const CART_TIERS_CODE = "__CART_TIERS__";

  let activeController = null;
  let isFetching = false;
  let pendingRefresh = false;
  // Set while WE re-render the cart (apply/remove), so the cart MutationObserver
  // ignores the #CartDrawer rebuild it would otherwise treat as an external
  // change and refresh on top of our own in-flight repaint.
  let suppressObserver = false;
  // Most recent /calculate-entries result, reused for instant repaint after an
  // apply/remove so chips don't flash empty while the proxy round-trips.
  let lastPreview = { totalEntries: 0, appliedCodes: [] };
  // Last known money-off amount (cents) per code. The /cart/update.js response
  // sometimes omits a surviving code's allocated amount right after a change,
  // which would blank the chip's "−$x.xx" for a beat; we fall back to this.
  const lastAmountByCode = new Map();
  // Codes attached to the cart but currently inapplicable (e.g. a non-combinable
  // boost deactivated by another discount). They render no chip, so codesFromDom
  // would miss them and apply/remove of ANOTHER code would silently drop them —
  // we stash them here so those paths preserve (and can reactivate) them.
  let lastInactiveCodes = [];

  /** Format an integer with comma thousand separators. */
  function formatEntries(n) {
    if (typeof n !== "number" || !Number.isFinite(n)) return "0";
    return Math.max(0, Math.round(n)).toLocaleString("en-US");
  }

  /** Pull the locale strings we need from the inline <script> the layout sets. */
  function readLocale() {
    const root = window.shftdCartLocale || {};
    return {
      invalid: root.invalid || "That code isn't valid right now.",
      applying: root.applying || "Applying…",
      apply: root.apply || "Apply",
      remove: root.remove || "Remove __CODE__",
    };
  }

  /** Trim a trailing ".0" so "2.0" → "2" but "1.5" stays "1.5". */
  function formatMultiplier(m) {
    return String(Math.round(m * 100) / 100);
  }

  /** Format an integer number of cents as currency (narrow symbol, e.g. $3.75). */
  function formatMoney(cents, currency) {
    const value = (Number(cents) || 0) / 100;
    try {
      return value.toLocaleString("en-US", {
        style: "currency",
        currency: currency || "USD",
        currencyDisplay: "narrowSymbol",
      });
    } catch (_err) {
      return "$" + value.toFixed(2);
    }
  }

  /**
   * Animate an element's number from its current value up/down to `to`,
   * writing comma-formatted text each frame. Stores the target on
   * `data-count-value` so a no-op (value unchanged) skips the tween and
   * concurrent calls cleanly hand off to the latest target.
   */
  function animateCount(el, to) {
    const target = Math.max(0, Math.round(to || 0));
    // Animate from what the shopper can SEE. data-count-value can go stale
    // (drawer re-renders, index-shifted seeds) and a stale 0 makes a settled
    // number visibly restart from zero.
    const visibleText = (el.textContent || "").replace(/[^\d]/g, "");
    const stored = Number(el.getAttribute("data-count-value"));
    const from = visibleText !== ""
      ? Number(visibleText)
      : Number.isFinite(stored)
        ? stored
        : 0;

    el.setAttribute("data-count-value", String(target));
    if (from === target) {
      el.textContent = formatEntries(target);
      return;
    }

    const reduced =
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) {
      el.textContent = formatEntries(target);
      return;
    }

    const DURATION = 420;
    const start = performance.now();
    function frame(now) {
      const t = Math.min(1, (now - start) / DURATION);
      const eased = 1 - Math.pow(1 - t, 3);
      el.textContent = formatEntries(from + (target - from) * eased);
      // Bail if a newer target superseded this tween.
      if (t < 1 && el.getAttribute("data-count-value") === String(target)) {
        requestAnimationFrame(frame);
      } else if (el.getAttribute("data-count-value") === String(target)) {
        el.textContent = formatEntries(target);
      }
    }
    requestAnimationFrame(frame);
  }

  const ENTRY_TOTAL_SELECTOR =
    "[data-entries-needs-refresh], .drawer__entries-count, .cart-sidebar__entries-count, .cart-sidebar__row-value--entries";

  /** Read an element's currently *visible* number (so we can animate from it). */
  function numFromEl(el) {
    const text = (el.textContent || "").replace(/[^\d]/g, "");
    if (text !== "") return Number(text);
    const dv = Number(el.getAttribute("data-count-value"));
    return Number.isFinite(dv) ? dv : null;
  }

  /**
   * Drawer re-renders (Section Rendering API) rebuild the entries banner +
   * capsules from their server-rendered (un-boosted) values, which would make
   * a boosted number snap down before the backend response animates it back.
   * We snapshot the live values before a re-render and re-seed them after, so
   * the only motion the shopper sees is one smooth count animation.
   */
  function snapshotEntryUI() {
    const totalEl = document.querySelector(ENTRY_TOTAL_SELECTOR);
    return {
      total: totalEl ? numFromEl(totalEl) : null,
      capsules: Array.from(
        document.querySelectorAll("#CartDrawer .entries-capsule__count, .cart-sidebar .entries-capsule__count")
      ).map(numFromEl),
    };
  }

  function seedEntryUI(snapshot) {
    if (!snapshot) return;
    if (snapshot.total != null) {
      document.querySelectorAll(ENTRY_TOTAL_SELECTOR).forEach(function (el) {
        el.setAttribute("data-count-value", String(snapshot.total));
        el.textContent = formatEntries(snapshot.total);
      });
    }
    document
      .querySelectorAll("#CartDrawer .entries-capsule__count, .cart-sidebar .entries-capsule__count")
      .forEach(function (el, i) {
        const v = snapshot.capsules[i];
        if (v != null) {
          el.setAttribute("data-count-value", String(v));
          el.textContent = formatEntries(v);
        }
      });
  }

  /**
   * A re-render replaces #CartDrawer.innerHTML, destroying the code <input>.
   * If the shopper had clicked/typed in it (e.g. lining up a second code right
   * after applying/removing one), the re-render lands a moment later and the
   * new element comes back blank + unfocused. Snapshot the focus + caret +
   * any typed value so we can restore them onto the freshly rendered input.
   */
  function snapshotCodeInputFocus() {
    const active = document.activeElement;
    if (!active || !active.matches || !active.matches("[data-cart-code-field]")) {
      return null;
    }
    return {
      id: active.id,
      value: active.value,
      start: active.selectionStart,
      end: active.selectionEnd,
    };
  }

  function restoreCodeInputFocus(snap) {
    if (!snap || !snap.id) return;
    const field = document.getElementById(snap.id);
    if (!field) return;
    try {
      // Only re-seed a typed value if the re-render came back empty, so we
      // never clobber a server-rendered state.
      if (snap.value && !field.value) field.value = snap.value;
      field.focus({ preventScroll: true });
      const end = snap.end != null ? snap.end : (field.value || "").length;
      const start = snap.start != null ? snap.start : end;
      field.setSelectionRange(start, end);
    } catch (_) {
      try { field.focus({ preventScroll: true }); } catch (_e) {}
    }
  }

  /** Re-render cart sections, preserving the live entries values across it. */
  function rerenderCartPreserving(state) {
    // Our own renderContents() replaces #CartDrawer's children, which the
    // observer watches. Suppress it for this synchronous block so it doesn't
    // queue a redundant refresh on top of the repaint we're about to do. The
    // observer callback runs as a microtask (before this macrotask clears the
    // flag), so it sees suppressObserver === true and bails.
    suppressObserver = true;
    const snap = snapshotEntryUI();
    const focusSnap = snapshotCodeInputFocus();
    rerenderCart(state);
    seedEntryUI(snap);
    restoreCodeInputFocus(focusSnap);
    setTimeout(function () { suppressObserver = false; }, 0);
  }

  /** Fetch /cart.js and return the parsed JSON, with a hard timeout. */
  async function fetchCart() {
    const ac = new AbortController();
    const timer = setTimeout(() => ac.abort(), FETCH_TIMEOUT_MS);
    try {
      const res = await fetch(CART_FETCH_PATH, {
        credentials: "same-origin",
        signal: ac.signal,
      });
      if (!res.ok) return null;
      return await res.json();
    } catch (_err) {
      return null;
    } finally {
      clearTimeout(timer);
    }
  }

  /** Call the proxy. Returns null on any error. */
  async function fetchPreview(payload, signal) {
    try {
      const res = await fetch(PROXY_PATH, {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        signal: signal,
      });
      if (!res.ok) return null;
      const data = await res.json();
      // Proxy wraps payloads as { success, data: {...} }. Be tolerant of both.
      if (data && data.success && data.data) return data.data;
      if (data && (data.totalEntries !== undefined || data.appliedCodes)) return data;
      return null;
    } catch (_err) {
      return null;
    }
  }

  /** Update (and animate) the entries banner numbers. */
  function paintEntries(totalEntries) {
    const total = Math.max(0, Math.round(totalEntries || 0));
    const targets = document.querySelectorAll(ENTRY_TOTAL_SELECTOR);
    targets.forEach(function (el) {
      animateCount(el, total);
      el.setAttribute("data-entries", String(total));
    });

    // Show or hide the entries banner depending on totalEntries > 0.
    const banners = document.querySelectorAll(
      ".drawer__entries-banner, .cart-sidebar__entries-banner"
    );
    banners.forEach(function (banner) {
      banner.toggleAttribute("hidden", !(totalEntries > 0));
    });
  }

  /**
   * Apply the active entry-boost to each per-line capsule, mirroring the backend
   * (entries.ts): codes MULTIPLY the line's base multiplier, and the cart-tier
   * ADDS a flat per-$ bonus on top —
   *     effective rate = baseMult × codeMult + tierAddPerDollar
   * (e.g. base 8 with a 2× code and a "+2" tier → 8×2 + 2 = 18×). The "BOOSTED"
   * glow shows whenever a code or a tier is live.
   *
   * The flat bonus_entries (+N) is order-level, so it only shows in the banner
   * total + the code chip — capsules reflect the per-$ rate only.
   */
  function applyCapsuleBoost(appliedCodes, tierAddPerDollar) {
    const active = Array.isArray(appliedCodes)
      ? appliedCodes.filter((c) => c && c.isEntryEffectActive)
      : [];
    let codeMult = 1;
    for (const c of active) {
      if (c.code === CART_TIERS_CODE) continue; // the tier is additive, not a code
      const m = Number(c.entryMultiplier);
      if (Number.isFinite(m) && m > codeMult) codeMult = m;
    }
    const tierAdd = Number(tierAddPerDollar) > 0 ? Number(tierAddPerDollar) : 0;
    const anyEffect = codeMult > 1 || tierAdd > 0;

    // Publish the cart-wide code multiplier for the cart-tiers progress bar, which
    // runs in a separate module and otherwise only sees the store base. Fire an
    // event when it changes so the bar repaints its rungs at base × code + tier add.
    if (window.SHFTD_APPLIED_CODE_MULT !== codeMult) {
      window.SHFTD_APPLIED_CODE_MULT = codeMult;
      try { document.dispatchEvent(new CustomEvent("shftd:entries-updated")); } catch (_e) {}
    }

    document.querySelectorAll("#CartDrawer .entries-capsule, .cart-sidebar .entries-capsule").forEach(function (cap) {
      const base = Number(cap.getAttribute("data-entries-base")) || 0;
      const countEl = cap.querySelector(".entries-capsule__count");
      const multEl = cap.querySelector(".entries-capsule__multiplier");
      const valEl = cap.querySelector(".entries-capsule__mult-value");
      const baseMult = multEl ? Number(multEl.getAttribute("data-mult-base")) || 0 : 0;

      // count = base×code + (base / baseMult)×tierAdd  ( = price × (baseMult×code + tierAdd) )
      let target = base;
      if (anyEffect) {
        const addEntries = baseMult > 0 ? (base / baseMult) * tierAdd : 0;
        target = Math.round(base * codeMult + addEntries);
      }
      if (countEl) animateCount(countEl, target);

      if (multEl && valEl && baseMult > 0) {
        const shown = anyEffect ? baseMult * codeMult + tierAdd : baseMult;
        valEl.textContent = formatMultiplier(shown);
      }

      cap.classList.toggle("is-boosted", anyEffect);
    });
  }

  /**
   * Predict the order's total entries client-side from the per-line capsule
   * bases + a set of entry effects (best-of, mirroring the backend). Used for
   * an optimistic total on remove, where every input is already known. Exact
   * for a single active sweepstake; the backend response reconciles otherwise.
   */
  function predictTotal(effects) {
    // Prefer the banner's server-rendered base (it already accounts for
    // quantity); fall back to summing the per-line capsule bases.
    let base = 0;
    const baseEl = document.querySelector(
      ".drawer__entries-count[data-entries-base], .cart-sidebar__entries-count[data-entries-base]"
    );
    const bannerBase = baseEl ? Number(baseEl.getAttribute("data-entries-base")) : NaN;
    if (Number.isFinite(bannerBase) && bannerBase > 0) {
      base = bannerBase;
    } else {
      document.querySelectorAll("#CartDrawer .entries-capsule, .cart-sidebar .entries-capsule").forEach(function (cap) {
        base += Number(cap.getAttribute("data-entries-base")) || 0;
      });
    }
    let best = base;
    (Array.isArray(effects) ? effects : []).forEach(function (ac) {
      if (!ac || !ac.isEntryEffectActive) return;
      const m = Number(ac.entryMultiplier);
      const b = Number(ac.bonusEntries);
      const mm = Number.isFinite(m) && m > 0 ? m : 1;
      const bb = Number.isFinite(b) && b > 0 ? b : 0;
      const cand = base * mm + bb;
      if (cand > best) best = cand;
    });
    return best;
  }

  // Mirror the backend's cumulative additive cart-tier (lib/cart-tiers.ts):
  // every met tier ADDS its per-$ value; bonuses sum. entry_multiplier_pct adds
  // base × value (the store base rate, same one the backend resolves against), so
  // the optimistic total tracks the rescaled ladder. Lets the optimistic total
  // re-derive the tier when a quantity change crosses a threshold.
  function cartTierEffectClient(subtotalCents) {
    const cfg = window.SHFTD_CART_TIERS;
    const base = Number(window.SHFTD_DEFAULT_MULTIPLIER) || 0;
    let add = 0, bonusFixed = 0, bonusFromBase = 0;
    if (cfg && cfg.enabled && Array.isArray(cfg.tiers)) {
      cfg.tiers.forEach(function (t) {
        if (subtotalCents < Number(t.minSubtotalCents)) return;
        var tierBonusBase = 0;
        (Array.isArray(t.effects) ? t.effects : []).forEach(function (e) {
          if (e.type === "entry_multiplier") add += Number(e.value) || 0;
          else if (e.type === "entry_multiplier_pct") add += base * (Number(e.value) || 0);
          else if (e.type === "bonus_entries") bonusFixed += Number(e.value) || 0;
          else if (e.type === "bonus_entries_base") tierBonusBase += Number(e.value) || 0;
        });
        // Round each tier's base-relative bonus UP to a clean 5 (matches backend + bar).
        var tb = base * tierBonusBase;
        bonusFromBase += tb > 0 ? Math.ceil(tb / 5) * 5 : 0;
      });
    }
    return { addPerDollar: add, bonus: bonusFixed + bonusFromBase };
  }

  // Instant optimistic boosted total from the live drawer DOM (the new qty),
  // mirroring the backend exactly: base × best-code + codeBonus +
  // round(effectiveSpend × tierAdd) + tierBonus, priced on the PRE-discount
  // original_price (the award basis). Exact for a single active sweepstake (the
  // common case); the debounced proxy reconciles anything else. This is what kills
  // the "base flashes, then re-animates to boosted a second later" lag.
  function predictBoostedFromCartDOM() {
    let base = 0, effSubtotal = 0, rawSubtotalCents = 0;
    document.querySelectorAll("#CartDrawer .cart-item, .cart-sidebar .cart-item").forEach(function (row) {
      const unitCents = parseInt(row.getAttribute("data-original-price") || row.getAttribute("data-unit-price"), 10) || 0;
      const mult = parseFloat(row.getAttribute("data-entries-multiplier")) || 0;
      const qi = row.querySelector(".quantity__input");
      const qty = qi ? parseInt(qi.value, 10) || 0 : 0;
      const unitDollars = unitCents / 100;
      const floor = Math.floor(unitDollars);
      const effPrice = floor % 10 === 9 ? floor + 1 : unitDollars;
      rawSubtotalCents += unitCents * qty;
      effSubtotal += effPrice * qty;
      if (mult > 0) base += Math.round(mult * effPrice) * qty;
    });
    let codeMult = 1, codeBonus = 0;
    (lastPreview.appliedCodes || []).forEach(function (ac) {
      if (!ac || !ac.isEntryEffectActive || ac.code === CART_TIERS_CODE) return;
      const m = Number(ac.entryMultiplier) || 1, b = Number(ac.bonusEntries) || 0;
      if (base * m + b > base * codeMult + codeBonus) { codeMult = m; codeBonus = b; }
    });
    const tier = cartTierEffectClient(rawSubtotalCents);
    return base * codeMult + codeBonus + Math.round(effSubtotal * tier.addPerDollar) + (tier.bonus || 0);
  }

  /**
   * Build the entry-effect label for a code chip:
   *   multiplier + bonus → "2X + 5 Entries"
   *   multiplier only    → "2X Boost"
   *   bonus only         → "100 Entries"
   */
  function entryEffectLabel(ac) {
    if (!ac || !ac.isEntryEffectActive) return "";
    const m = Number(ac.entryMultiplier);
    const b = Number(ac.bonusEntries);
    const hasM = Number.isFinite(m) && m > 1;
    const hasB = Number.isFinite(b) && b > 0;
    if (hasM && hasB) {
      return formatMultiplier(m) + "X + " + formatEntries(b) + " Entries";
    }
    if (hasM) return formatMultiplier(m) + "X Boost";
    if (hasB) return formatEntries(b) + " Entries";
    return "";
  }

  /**
   * Render the applied-code chips from live cart data.
   *
   * The Liquid `cart` object has no `discount_codes`, so chips can't be
   * server-rendered. We build them here from /cart.js (`discount_codes` for the
   * applied list, `cart_level_discount_applications` for money-off amounts) and
   * the /calculate-entries response (entry-boost badges). Built with DOM APIs +
   * textContent so merchant/customer-controlled code strings can't inject HTML.
   */
  /** Create the stable DOM skeleton for one chip (fields filled by update). */
  function buildChip(code, locale) {
    const li = document.createElement("li");
    li.className = "cart-code-input__applied-item";
    li.setAttribute("data-applied-code", code);

    const codeSpan = document.createElement("span");
    codeSpan.className = "cart-code-input__applied-code";
    codeSpan.textContent = code.length > 8 ? code.slice(0, 8) + "\u2026" : code;
    codeSpan.title = code;
    li.appendChild(codeSpan);

    const eff = document.createElement("span");
    eff.className = "cart-code-input__effect";
    eff.hidden = true;
    li.appendChild(eff);

    const amt = document.createElement("span");
    amt.className = "cart-code-input__amount";
    amt.hidden = true;
    li.appendChild(amt);

    const removeBtn = document.createElement("button");
    removeBtn.type = "button";
    removeBtn.className = "cart-code-input__remove";
    removeBtn.setAttribute("data-cart-code-remove", "");
    removeBtn.setAttribute("data-code", code);
    removeBtn.setAttribute("aria-label", locale.remove.replace(/__CODE__/g, code));
    const x = document.createElement("span");
    x.setAttribute("aria-hidden", "true");
    x.textContent = "\u00d7";
    removeBtn.appendChild(x);
    li.appendChild(removeBtn);

    return li;
  }

  /** Patch a chip's meta in place: show the entry badge, else the saving. */
  function updateChipMeta(li, effectLabel, amountCents, currency) {
    const eff = li.querySelector(".cart-code-input__effect");
    const amt = li.querySelector(".cart-code-input__amount");
    if (effectLabel) {
      if (eff.textContent !== effectLabel) eff.textContent = effectLabel;
      eff.hidden = false;
      amt.hidden = true;
    } else if (amountCents > 0) {
      const text = "\u2212" + formatMoney(amountCents, currency);
      if (amt.textContent !== text) amt.textContent = text;
      amt.hidden = false;
      eff.hidden = true;
    } else {
      eff.hidden = true;
      amt.hidden = true;
    }
  }

  /**
   * Surface a cue when a code is attached to the cart but NOT applicable.
   *
   * This is intended behaviour, not a fault: SHFTD's boost codes are configured
   * not to combine, so when one collides with another discount Shopify keeps it
   * on the cart but flips `applicable:false`. We deliberately don't count those
   * toward entries (the proxy payload filters to applicable codes) and they get
   * no chip — but without a word the shopper just sees their boost vanish. This
   * renders a small note, per cart-code-input, naming the deactivated code(s).
   * Idempotent: it reuses/clears one `.cart-code-input__notice` per container.
   */
  function updateInactiveNotice(root, inactiveCodes) {
    const host = root.parentElement;
    if (!host) return;
    let notice = host.querySelector(".cart-code-input__notice");

    if (!inactiveCodes || inactiveCodes.length === 0) {
      if (notice) notice.remove();
      return;
    }

    if (!notice) {
      notice = document.createElement("p");
      notice.className = "cart-code-input__notice";
      notice.setAttribute("role", "status");
      notice.setAttribute("aria-live", "polite");
      // Sit the note directly beneath the chips list.
      host.insertBefore(notice, root.nextSibling);
    }

    // Crop each code the same way chips do, so a long/hostile code can't blow
    // out the layout. textContent only — never innerHTML — for the code string.
    const names = inactiveCodes
      .map(function (c) {
        const code = String(c.code || "");
        return code.length > 8 ? code.slice(0, 8) + "…" : code;
      })
      .join(", ");
    const msg = names + " can’t be combined with another discount.";
    if (notice.textContent !== msg) notice.textContent = msg;
  }

  /**
   * Reconcile the applied-code chips against live cart data — idempotent, so
   * the reconcile pass after a code change reuses existing chip DOM and only
   * patches changed fields (no full teardown/rebuild flash). New chips are
   * appended; gone chips removed; the entry badge fades in on its existing
   * chip when /calculate-entries returns.
   *
   * The Liquid `cart` object has no `discount_codes`, so chips are built here
   * from /cart.js (`discount_codes` + `cart_level_discount_applications`) and
   * the /calculate-entries response (entry-boost badges). textContent only, so
   * code strings can't inject HTML.
   */
  function renderAppliedCodes(cart, appliedCodes) {
    const roots = document.querySelectorAll("[data-cart-applied-codes]");
    if (roots.length === 0) return;

    const locale = readLocale();
    const currency = (cart && cart.currency) || "USD";

    const codes =
      cart && Array.isArray(cart.discount_codes)
        ? cart.discount_codes.filter((d) => d && d.code && d.applicable)
        : [];

    // Codes attached but deactivated by Shopify (a non-combinable boost code
    // that collided with another discount). Shown as a cue, never as a chip.
    const inactiveCodes =
      cart && Array.isArray(cart.discount_codes)
        ? cart.discount_codes.filter((d) => d && d.code && !d.applicable)
        : [];
    // Remember them so apply/remove of another code preserves (not drops) them.
    lastInactiveCodes = inactiveCodes.map((d) => d.code);

    // Money-off amount per code (cents), matched by discount title == code.
    const amountByCode = new Map();
    const apps =
      cart && Array.isArray(cart.cart_level_discount_applications)
        ? cart.cart_level_discount_applications
        : [];
    for (const app of apps) {
      if (app && typeof app.title === "string") {
        amountByCode.set(app.title.toUpperCase(), Number(app.total_allocated_amount) || 0);
      }
    }

    // Entry-boost effect per code from /calculate-entries.
    const effectByCode = new Map();
    if (Array.isArray(appliedCodes)) {
      for (const ac of appliedCodes) {
        if (ac && typeof ac.code === "string") effectByCode.set(ac.code.toUpperCase(), ac);
      }
    }

    roots.forEach(function (root) {
      // Always reconcile the deactivated-code cue first — it must show even when
      // there are zero applicable chips (e.g. the only code applied collided).
      updateInactiveNotice(root, inactiveCodes);

      if (codes.length === 0) {
        root.textContent = "";
        root.classList.add("is-empty");
        return;
      }
      root.classList.remove("is-empty");

      // Index the chips already in the DOM so we can reuse them.
      const existing = new Map();
      root.querySelectorAll(".cart-code-input__applied-item").forEach((li) => {
        existing.set((li.getAttribute("data-applied-code") || "").toUpperCase(), li);
      });

      const keep = new Set();
      codes.forEach(function (d) {
        const key = d.code.toUpperCase();
        keep.add(key);
        let li = existing.get(key);
        if (!li) {
          li = buildChip(d.code, locale);
          existing.set(key, li);
        }
        // Resolve the saving: prefer this payload's value, else the last known
        // (avoids a blank "−$x.xx" when update.js omits it mid-transition).
        const cents = amountByCode.has(key)
          ? amountByCode.get(key)
          : lastAmountByCode.get(key) || 0;
        if (cents > 0) lastAmountByCode.set(key, cents);
        else lastAmountByCode.delete(key);

        updateChipMeta(
          li,
          entryEffectLabel(effectByCode.get(key)),
          cents,
          currency
        );
        // appendChild on an existing node MOVES it (keeps order, no recreate).
        root.appendChild(li);
      });

      existing.forEach((li, key) => {
        if (!keep.has(key)) {
          lastAmountByCode.delete(key);
          li.remove();
        }
      });
    });
  }

  /**
   * Hide cart_level_discount_applications rows whose value is "$0.00" — these
   * are entry-effect-only codes (no money discount). The applied-code pill
   * already shows them, so the legacy "-$0.00" line would be confusing.
   */
  function hideZeroDollarDiscountRows() {
    const items = document.querySelectorAll(".discounts__discount");
    items.forEach(function (li) {
      const text = (li.textContent || "").replace(/\s+/g, "");
      // Match "(-$0.00)" or "(-$0,00)" etc — any zero-amount currency.
      if (/\(-?\$?0[.,]00\)?/.test(text)) {
        li.classList.add("is-entry-effect-only");
      } else {
        li.classList.remove("is-entry-effect-only");
      }
    });
  }

  /** Build the lineItems body for the proxy. */
  function buildLineItemsPayload(cart) {
    if (!cart || !Array.isArray(cart.items)) return [];
    return cart.items.map(function (it) {
      return {
        sku: it.sku || "",
        title: it.product_title || it.title || "",
        // PRE-discount unit price (original_price), in dollars. The order webhook
        // awards entries on the list price, not the discounted price, so the
        // preview must use the same basis or a %-off code makes the cart total
        // disagree with what's awarded. Falls back to final_price/price.
        price: ((it.original_price != null ? it.original_price : it.final_price != null ? it.final_price : it.price || 0) / 100).toFixed(2),
        quantity: it.quantity,
        product_id: it.product_id ? String(it.product_id) : undefined,
      };
    });
  }

  /** Build the applied-code list, filtered to applicable entries only. */
  function buildAppliedCodes(cart) {
    if (!cart || !Array.isArray(cart.discount_codes)) return [];
    return cart.discount_codes
      .filter(function (d) {
        return d && d.code && d.applicable;
      })
      .map(function (d) {
        return d.code;
      });
  }

  /**
   * Given a cart object (from /cart.js OR a /cart/update.js response), fetch the
   * entries preview and repaint. Callers that already hold a fresh cart (apply /
   * remove) can use this to skip a redundant /cart.js round-trip.
   */
  async function computeAndPaint(cart, codesOverride) {
    if (!cart) return;

    if (activeController) activeController.abort();
    activeController = new AbortController();
    const ac = activeController;

    // The code set sent to the proxy (entries are computed from exactly this).
    // Prefer the caller's intended set over cart.discount_codes — the
    // /cart/update.js response can briefly still list a just-removed code as
    // applicable, which would spike the total before settling — but also drop
    // anything Shopify marks inapplicable (e.g. a just-deactivated
    // non-combinable boost), so entries never count a code that won't apply at
    // checkout. The two compose: send (intended set) ∩ (still-applicable).
    let codes;
    if (Array.isArray(codesOverride)) {
      const applicable = new Set(
        (cart.discount_codes || [])
          .filter((d) => d && d.code && d.applicable)
          .map((d) => d.code.toUpperCase())
      );
      codes = codesOverride.filter((c) => applicable.has(c.toUpperCase()));
    } else {
      codes = buildAppliedCodes(cart);
    }

    // Render chips from the intended code set too, so a stale code lingering in
    // state.discount_codes can't flash a removed chip back in.
    let renderCart = cart;
    if (Array.isArray(codesOverride)) {
      const overrideSet = new Set(codesOverride.map((c) => c.toUpperCase()));
      renderCart = Object.assign({}, cart, {
        discount_codes: (cart.discount_codes || []).filter(
          (d) => d && d.code && overrideSet.has(d.code.toUpperCase())
        ),
      });
    }

    // Render the chips up front from the cart alone — the chip's existence,
    // code text and money-off saving all come from /cart.js, not the proxy.
    // Doing this BEFORE the proxy round-trip means an applied code is always
    // visible even if /calculate-entries is slow, errors, or times out; the
    // only thing the proxy adds is the entry-boost badge, which fades in below
    // once it returns. (Reuses the last known effects so a badge doesn't blink.)
    renderAppliedCodes(renderCart, lastPreview.appliedCodes);

    // Bound the proxy call: an unreachable/hung backend must not leave the UI
    // stuck mid-update. On timeout we abort, fetchPreview returns null, and the
    // chips rendered above remain as the durable state.
    const proxyTimeout = setTimeout(function () { ac.abort(); }, FETCH_TIMEOUT_MS);
    let data;
    try {
      data = await fetchPreview(
        {
          lineItems: buildLineItemsPayload(cart),
          appliedDiscountCodes: codes,
          // PRE-discount subtotal (original_total_price) so tier qualification
          // matches the webhook award, which uses pre-discount line prices.
          // (items_subtotal_price is AFTER line discounts — would mis-qualify a
          // discounted cart vs the award.) Falls back if the field is absent.
          subtotalCents: Number(cart && (cart.original_total_price != null ? cart.original_total_price : cart.items_subtotal_price)) || 0,
        },
        ac.signal
      );
    } finally {
      clearTimeout(proxyTimeout);
    }
    // A newer computeAndPaint started while we awaited the proxy, so its result
    // supersedes ours. Bail before painting — otherwise an older/slower response
    // (computed for the PREVIOUS subtotal/tier) can land last and win, which is
    // the "previous tier's effect shows" bug + the quantity over-correction.
    // The abort() at the top cancels in-flight calls; this also catches the race
    // where our fetch resolved a beat before being aborted.
    if (ac !== activeController) return;
    if (!data) {
      // Proxy unreachable/slow ON A CODE CHANGE: we can't confirm the new entry
      // effects, so fall back to the un-boosted base rather than leaving a stale
      // boost on screen for a code that may no longer apply (the chips rendered
      // above from the cart stay — only the boost numbers reset). Clearing
      // lastPreview stops the optimistic apply/remove path reusing stale
      // effects. Self-heals on the next successful refresh. A plain refresh with
      // no code change just leaves the last good numbers — no transient flicker.
      if (Array.isArray(codesOverride)) {
        lastPreview = { totalEntries: 0, appliedCodes: [], tierAdd: 0 };
        applyCapsuleBoost([], 0);
        paintEntries(predictTotal([]));
      }
      return;
    }

    const tierAdd = (data.cartTier && Number(data.cartTier.addPerDollar)) || 0;
    lastPreview = {
      totalEntries: data.totalEntries,
      appliedCodes: Array.isArray(data.appliedCodes) ? data.appliedCodes : [],
      tierAdd: tierAdd,
    };
    paintEntries(data.totalEntries);
    renderAppliedCodes(renderCart, data.appliedCodes);
    applyCapsuleBoost(data.appliedCodes, tierAdd);
    hideZeroDollarDiscountRows();
  }

  /** Re-fetch cart + preview and repaint UI (used on load + cart events). */
  async function refresh() {
    if (isFetching) {
      pendingRefresh = true;
      return;
    }
    isFetching = true;
    pendingRefresh = false;

    try {
      const cart = await fetchCart();
      await computeAndPaint(cart);
    } finally {
      isFetching = false;
      if (pendingRefresh) refresh();
    }
  }

  /**
   * Which cart sections (Section Rendering API) should be refreshed after a
   * discount code changes. Only includes containers actually present on the
   * page, so this works for both the cart drawer and the cart page.
   */
  function cartSectionNames() {
    const names = [];
    if (document.querySelector("cart-drawer #CartDrawer")) names.push("cart-drawer");
    if (document.getElementById("cart-icon-bubble")) names.push("cart-icon-bubble");
    const footer = document.getElementById("main-cart-footer");
    if (footer && footer.dataset.id) names.push(footer.dataset.id);
    const items = document.getElementById("main-cart-items");
    if (items && items.dataset.id) names.push(items.dataset.id);
    // De-dupe (footer + items can share a section id in some templates).
    return names.filter((n, i) => names.indexOf(n) === i);
  }

  /** Extract an element's innerHTML from a rendered section string. */
  function sectionInnerHTML(html, selector) {
    if (!html) return null;
    const doc = new DOMParser().parseFromString(html, "text/html");
    const el = selector ? doc.querySelector(selector) : doc.body;
    return el ? el.innerHTML : null;
  }

  /**
   * Re-render the cart UI from a /cart/update.js response that included
   * `sections`. This is what makes applied-code chips appear and money-off
   * totals update after applying a code.
   *
   *  - Drawer: reuse Dawn's CartDrawer.renderContents so the overlay/close
   *    handlers and the header cart bubble re-bind correctly.
   *  - Cart page: swap the footer + line-items section innerHTML directly.
   *
   * Delegated submit/click handlers live on document.body, so they survive
   * these innerHTML replacements without re-wiring.
   */
  function rerenderCart(state) {
    if (!state || !state.sections) return;

    const cartDrawer = document.querySelector("cart-drawer");
    if (
      cartDrawer &&
      typeof cartDrawer.renderContents === "function" &&
      state.sections["cart-drawer"]
    ) {
      cartDrawer.renderContents(state);
    }

    const footer = document.getElementById("main-cart-footer");
    if (footer && footer.dataset.id && state.sections[footer.dataset.id]) {
      const inner = sectionInnerHTML(state.sections[footer.dataset.id], "#main-cart-footer");
      if (inner != null) footer.innerHTML = inner;
    }

    const items = document.getElementById("main-cart-items");
    if (items && items.dataset.id && state.sections[items.dataset.id]) {
      const contents = items.querySelector(".js-contents");
      const inner = sectionInnerHTML(state.sections[items.dataset.id], ".js-contents");
      if (contents && inner != null) contents.innerHTML = inner;
    }
  }

  /**
   * Set the cart's full discount-code set via /cart/update.js.
   *
   * Shopify's `discount` param REPLACES all codes, and stacks via a
   * comma-separated list (see Ajax cart docs). So callers pass the complete
   * desired set; we join it. An empty array clears all codes. Requests fresh
   * section HTML so the drawer/total/chips can re-render.
   */
  async function setDiscounts(codes) {
    const res = await fetch("/cart/update.js", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        discount: codes.join(","),
        sections: cartSectionNames(),
        sections_url: window.location.pathname,
      }),
    });
    if (!res.ok) return { ok: false, state: null };
    const state = await res.json().catch(() => null);
    return { ok: true, state };
  }

  /**
   * Apply a discount code, STACKING it onto any codes already on the cart
   * (Shopify replaces the whole set per request, so we send existing + new),
   * then re-render the cart so the new chip + any money-off total show.
   */
  async function applyCode(formEl, code) {
    const applyBtn = formEl.querySelector("[data-cart-code-apply]");
    const applyText = formEl.querySelector("[data-cart-code-apply-text]");
    const errorEl = formEl.querySelector("[data-cart-code-error]");
    const fieldEl = formEl.querySelector("[data-cart-code-field]");
    const locale = readLocale();

    if (errorEl) {
      errorEl.textContent = "";
      errorEl.setAttribute("hidden", "");
    }
    if (applyBtn) applyBtn.setAttribute("aria-busy", "true");
    if (applyText) applyText.textContent = locale.applying;

    try {
      // Existing codes come straight from the rendered chips — no /cart.js wait.
      const existing = codesFromDom();
      const next = existing.slice();
      if (!existing.some((c) => c.toUpperCase() === code.toUpperCase())) {
        next.push(code);
      }
      const { ok, state } = await setDiscounts(next);
      if (!ok) {
        if (errorEl) {
          errorEl.textContent = locale.invalid;
          errorEl.removeAttribute("hidden");
        }
        return;
      }
      if (fieldEl) fieldEl.value = "";
      rerenderCartPreserving(state);
      // Repaint chips synchronously from the update.js cart so the freshly
      // re-rendered (empty) container never flashes blank, then compute entries
      // straight from that cart (no extra /cart.js round-trip).
      renderAppliedCodes(state, lastPreview.appliedCodes);
      await computeAndPaint(state, next);
    } catch (_err) {
      if (errorEl) {
        errorEl.textContent = locale.invalid;
        errorEl.removeAttribute("hidden");
      }
    } finally {
      if (applyBtn) applyBtn.removeAttribute("aria-busy");
      if (applyText) applyText.textContent = locale.apply;
    }
  }

  /**
   * Read the applied codes straight from the rendered chips (no network), plus
   * any attached-but-inactive codes (which render no chip). Including the latter
   * means removing a conflicting code reactivates a deactivated boost instead of
   * silently deleting it, and applying another code doesn't drop it either.
   */
  function codesFromDom() {
    const seen = new Set();
    const codes = [];
    function add(c) {
      const code = c || "";
      const key = code.toUpperCase();
      if (code && !seen.has(key)) {
        seen.add(key);
        codes.push(code);
      }
    }
    document
      .querySelectorAll("[data-cart-applied-codes] [data-applied-code]")
      .forEach(function (el) {
        add(el.getAttribute("data-applied-code") || "");
      });
    lastInactiveCodes.forEach(add);
    return codes;
  }

  /**
   * Remove a single code (the chip's × button carries `data-code`), keeping the
   * other applied codes. Falls back to clearing all if no code is specified.
   *
   * Optimistic: the chip vanishes + capsules de-boost instantly (derived from
   * the DOM, no /cart.js round-trip first). We then POST the new set; on
   * success we reconcile, and on failure a fresh refresh() restores the real
   * cart state (the code reappears).
   */
  async function removeCode(btn) {
    const target = (btn.getAttribute("data-code") || "").toUpperCase();
    const item = btn.closest(".cart-code-input__applied-item");
    const existing = codesFromDom();
    const next = target
      ? existing.filter((c) => c.toUpperCase() !== target)
      : [];

    // Optimistic boost must reflect only codes that are ACTIVE right now (have a
    // chip) and survive this removal — never an inactive code, whose effect in
    // lastPreview can be stale and would flash a deactivated boost back on. (The
    // inactive codes still ride along in `next` for the POST, so removing a
    // blocker can still reactivate them — the backend response confirms it.)
    const activeSurvivors = new Set(
      Array.from(
        document.querySelectorAll("[data-cart-applied-codes] [data-applied-code]")
      )
        .map((el) => (el.getAttribute("data-applied-code") || "").toUpperCase())
        .filter((c) => c && c !== target)
    );
    const remainingEffects = lastPreview.appliedCodes.filter(
      (ac) =>
        ac &&
        typeof ac.code === "string" &&
        activeSurvivors.has(ac.code.toUpperCase())
    );

    // --- Optimistic UI: drop the chip + animate capsules AND the total to
    // their predicted post-removal values right away (no backend wait). ---
    if (item) item.remove();
    document.querySelectorAll("[data-cart-applied-codes]").forEach(function (root) {
      if (!root.querySelector(".cart-code-input__applied-item")) {
        root.classList.add("is-empty");
      }
    });
    // Removing a code doesn't change the cart subtotal, so the tier add is
    // unchanged — reuse the last known value for the optimistic repaint.
    applyCapsuleBoost(remainingEffects, lastPreview && lastPreview.tierAdd);
    paintEntries(predictTotal(remainingEffects));

    try {
      const { ok, state } = await setDiscounts(next);
      if (!ok) throw new Error("discount update failed");
      // Re-render totals (money-off codes change the cart total), preserving
      // the live entries values so they animate smoothly to the new total
      // instead of snapping to the server-rendered base first.
      rerenderCartPreserving(state);
      renderAppliedCodes(state, remainingEffects);
      // Compute entries straight from the update.js cart (no extra /cart.js),
      // using the intended code set so a stale code can't spike the total.
      await computeAndPaint(state, next);
    } catch (_err) {
      // Backend rejected the change — restore the true state.
      await refresh();
    }
  }

  /**
   * Event delegation on document.
   *
   * We attach exactly one submit + one click listener to document.body and
   * match by data attribute on the event target's closest ancestor. This
   * survives dynamic re-renders of the cart drawer (Dawn's cart-drawer.js
   * replaces #CartDrawer.innerHTML on every cart update) without us having
   * to re-wire forms after each render.
   */
  function bindDelegatedHandlers() {
    if (document.body.dataset.shftdCartWired === "1") return;
    document.body.dataset.shftdCartWired = "1";

    document.addEventListener(
      "submit",
      function (e) {
        const form = e.target instanceof Element
          ? e.target.closest("[data-cart-code-form]")
          : null;
        if (!form) return;
        e.preventDefault();
        const fieldEl = form.querySelector("[data-cart-code-field]");
        if (!fieldEl) return;
        const code = (fieldEl.value || "").trim();
        if (!code) return;
        applyCode(form, code);
      },
      true // capture phase, so we run before any other handler that might re-submit
    );

    document.addEventListener("click", function (e) {
      const btn = e.target instanceof Element
        ? e.target.closest("[data-cart-code-remove]")
        : null;
      if (!btn) return;
      e.preventDefault();
      removeCode(btn);
    });
  }

  function init() {
    bindDelegatedHandlers();
    // Run on initial load to overwrite the SSR placeholder with live numbers.
    refresh();
  }

  /**
   * The reliable repaint trigger. Add-to-cart (product-form / quick-add),
   * single-line removal (cart-drawer.js `_rerenderDrawer`), and Dawn's
   * `renderContents` all rebuild the drawer by replacing #CartDrawer.innerHTML
   * — which destroys the client-rendered applied-code chips. None of them emit
   * a DOM event we can rely on: the add paths announce via the theme's
   * in-memory `publish()`, which never reaches `document` listeners, and the
   * removal path emits nothing. So rather than chase event names, we watch
   * #CartDrawer itself for child-list changes — a full re-render replaces its
   * direct children and fires this; our own repaints (chips, capsule classes,
   * count text) all mutate deep inside the drawer, never at the direct-child
   * level, so `{ childList: true }` (no subtree) never sees them and can't
   * loop. `suppressObserver` is a second guard for our own re-renders.
   */
  function observeCartReRenders() {
    const drawer = document.getElementById("CartDrawer");
    if (drawer && typeof MutationObserver === "function") {
      let debounce = null;
      const obs = new MutationObserver(function () {
        if (suppressObserver) return;
        clearTimeout(debounce);
        debounce = setTimeout(refresh, 150);
      });
      obs.observe(drawer, { childList: true });
    }

    // Cart PAGE (not the drawer): Dawn's cart.js patches line items in place
    // and announces it through the theme's in-memory pubsub, not a DOM event.
    // Subscribe so the cart-page chips repaint after a quantity change too.
    if (
      typeof subscribe === "function" &&
      typeof PUB_SUB_EVENTS === "object" &&
      PUB_SUB_EVENTS &&
      PUB_SUB_EVENTS.cartUpdate
    ) {
      subscribe(PUB_SUB_EVENTS.cartUpdate, refresh);
    }

    // Drawer quantity +/-: cart-drawer.js patches the entries banner from the
    // per-line multipliers alone (its `_updateEntries`), which silently drops
    // any active boost — and an in-place qty change does NOT replace
    // #CartDrawer.innerHTML, so the observer above never sees it. Reconcile
    // against the backend after cart-drawer.js's own 400ms debounce +
    // /cart/change.js settle, so the boosted total + capsules come back. (The
    // cart PAGE qty path publishes cartUpdate, already handled above.)
    let qtyTimer = null;
    function onQtyChange() {
      // Paint the correct BOOSTED total INSTANTLY (no base flash, nothing to
      // re-animate when the backend returns), then reconcile once cart-drawer's
      // 400ms /cart/change.js settles. If the optimistic matched, animateCount
      // sees from === to and does nothing — so no jarring re-animation.
      paintEntries(predictBoostedFromCartDOM());
      clearTimeout(qtyTimer);
      qtyTimer = setTimeout(refresh, 1100);
    }
    // QuantityInput dispatches `change` on the input after a +/- step (value
    // already applied) and on manual edits, so this one hook covers both.
    document.addEventListener("change", function (e) {
      const inp =
        e.target instanceof Element
          ? e.target.closest('#CartDrawer .quantity__input')
          : null;
      if (inp) onQtyChange();
    });
  }

  // Cart updates: Dawn-derived themes dispatch a few different events. We
  // listen broadly and dedupe via the in-flight flag.
  function bindCartEvents() {
    const events = [
      "cart:update",
      "cart:refresh",
      "cart-drawer:opened",
      "cart-drawer:updated",
    ];
    events.forEach(function (ev) {
      document.addEventListener(ev, refresh);
    });

    // The robust trigger: a MutationObserver on #CartDrawer (covers every
    // add/remove path, which all replace its innerHTML) + the cart-page pubsub.
    observeCartReRenders();

    // Catch-all submit observer for Dawn-style add-to-cart / line-item
    // change forms whose own handlers we don't control. Skips OUR form
    // (data-cart-code-form) because applyCode() already calls refresh()
    // synchronously after the POST settles — wiring this would refresh
    // twice on every Apply click.
    document.addEventListener("submit", function (e) {
      const form = e.target instanceof Element ? e.target : null;
      if (!form) return;
      if (form.matches("[data-cart-code-form]")) return;
      if (form.matches(
        'form[action="/cart/update.js"], form[action="/cart/change.js"], form[action$="/cart/add.js"]'
      )) {
        setTimeout(refresh, 600);
      }
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () {
      init();
      bindCartEvents();
    });
  } else {
    init();
    bindCartEvents();
  }
})();
